import { Fragment, useEffect, useMemo, useState } from "react";
import { FilterComponentProps } from "./DreamSearchFilter";
import debounce from "lodash/debounce";
import { useDreamSearchContext } from "../../../DreamSearchContext";
import Chip from "@mui/material/Chip";
import { RANKED_HEALTH_VOCATIONS } from "@portal/app/_common/lib/knownVocations";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose } from "@fortawesome/pro-solid-svg-icons/faClose";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { kebabCase } from "@portal/app/_common/lib/changeCase";
import ListItem from "@mui/material/ListItem";
import Box from "@mui/material/Box";
import { faMagnifyingGlass } from "@fortawesome/pro-solid-svg-icons/faMagnifyingGlass";
import useAutocomplete, {
  AutocompleteGroupedOption,
  createFilterOptions,
} from "@mui/material/useAutocomplete";

type Vocation = {
  id: string;
  adjective: string;
  abbreviation: string | null;
};

export const ProgramTypeFilterComponent = ({
  value,
  setValue,
  setHasOptions,
}: FilterComponentProps<string[]>) => {
  const { filterSettings } = useDreamSearchContext();
  const [localValue, setLocalValue] = useState<string[]>(value);

  const debounceSetValue = useMemo(
    () => debounce((localValue: string[]) => setValue(localValue), 1000),
    [setValue]
  );

  const [vocationsAvailable] = useState(filterSettings?.vocation ?? []);
  const vocationOptionIds = vocationsAvailable.map((vocation) => vocation.id);
  const vocationOptionMap = vocationsAvailable.reduce(
    (acc, vocation) => ({ ...acc, [vocation.id]: vocation }),
    {}
  ) as Record<string, Vocation>;

  /**
   * Sort by RANKED_HEALTH_VOCATIONS first, then alphabetically
   */
  const sortedRankedOptions: string[] = !vocationOptionMap
    ? []
    : [...vocationOptionIds].sort((a, b) => {
        if (RANKED_HEALTH_VOCATIONS.includes(a) && !RANKED_HEALTH_VOCATIONS.includes(b)) {
          return -1;
        }
        if (RANKED_HEALTH_VOCATIONS.includes(b) && !RANKED_HEALTH_VOCATIONS.includes(a)) {
          return 1;
        }
        const aLabel = vocationOptionMap[a].abbreviation ?? vocationOptionMap[a].adjective;
        const bLabel = vocationOptionMap[b].abbreviation ?? vocationOptionMap[b].adjective;
        return aLabel < bLabel ? -1 : aLabel > bLabel ? 1 : 0;
      });

  /**
   * When there are MAX_OPTIONS number of vocationOptions or more, we will show
   * an Autocomplete to filter down the options. It any case, we will only ever
   * show MAX_OPTIONS resulting options to chose from.
   */
  const { inputValue, groupedOptions, getInputProps, getOptionProps, getClearProps } =
    useAutocomplete<string, true, false>({
      value: localValue.filter((vocationId) => vocationOptionIds.includes(vocationId)),
      onChange: (event, newValue) => {
        event.preventDefault();
        event.stopPropagation();
        setLocalValue(newValue);
        debounceSetValue(newValue);
      },
      open: true,
      options: sortedRankedOptions,
      multiple: true,
      clearOnBlur: false,
      filterOptions: createFilterOptions({
        stringify: (option) =>
          vocationOptionMap?.[option].adjective ?? vocationOptionMap?.[option].abbreviation ?? "",
        trim: true,
      }),
      groupBy: (option) =>
        RANKED_HEALTH_VOCATIONS.includes(option) ? "Top programs" : "Other programs",
    });
  const inputProps = getInputProps();
  const optionsShown = groupedOptions.length;

  useEffect(() => {
    if (filterSettings) {
      setHasOptions(!!vocationOptionIds.length);
    }
  }, [vocationOptionIds, filterSettings, setHasOptions]);

  const Option = ({ vocationId }: { vocationId: string }) => {
    const [checked, setChecked] = useState(localValue.includes(vocationId));
    const friendlyKey =
      vocationOptionMap?.[vocationId].adjective ?? vocationOptionMap?.[vocationId].abbreviation;
    const { onClick, ...optionProps } = getOptionProps({
      option: vocationId,
      index: groupedOptions
        .flatMap((groupOption) => (groupOption as AutocompleteGroupedOption).options)
        .indexOf(vocationId),
    });
    const handleClick = (e: React.MouseEvent<HTMLLIElement, MouseEvent>) => {
      e.preventDefault();
      e.stopPropagation();
      onClick?.(e);
      setChecked(!localValue.includes(vocationId));
    };

    useEffect(() => {
      setChecked(localValue.includes(vocationId));
    }, [setChecked, vocationId]);

    return (
      <OptionListItem {...optionProps} key={vocationId} onClick={handleClick}>
        <StyledChip
          label={friendlyKey}
          data-testid={kebabCase(`program-type-option-${friendlyKey}`)}
          className={checked ? "selected" : ""}
        />
      </OptionListItem>
    );
  };

  return (
    <Container>
      <Header>Program type</Header>
      <AutocompleteTextField
        placeholder="Search by program type"
        variant="standard"
        slotProps={{
          htmlInput: {
            ...inputProps,
          },
          input: {
            disableUnderline: true,
            onBlurCapture: (e) => {
              e.preventDefault();
              e.stopPropagation();
            },
            startAdornment: (
              <Box
                sx={{
                  pr: "1rem",
                  color: "#ababab" /* color of placeholder */,
                }}
              >
                <FontAwesomeIcon icon={faMagnifyingGlass} />
              </Box>
            ),
            endAdornment: (
              <EndAdornmentContainer>
                {inputValue && (
                  <>
                    <Chip
                      label={`Showing ${optionsShown} of ${vocationOptionIds.length}`}
                      size="small"
                    />
                    <IconButton
                      {...getClearProps()}
                      component="button"
                      size="small"
                      color="primary"
                    >
                      <FontAwesomeIcon icon={faClose} />
                    </IconButton>
                  </>
                )}
              </EndAdornmentContainer>
            ),
          },
        }}
        data-testid="program-type-filter-search"
      />

      <OptionsContainer>
        {(groupedOptions as AutocompleteGroupedOption[]).map(({ group, options }) => (
          <Fragment key={group}>
            {/* MUI maps `subtitle2` to an h6 by default and the theme does not override it, so
                these group labels need an explicit span. */}
            <Typography
              variant="subtitle2"
              component="span"
              sx={({ palette }) => ({ my: 1, color: palette.dream.grayMediumDark })}
            >
              {group}
            </Typography>
            <ChipRow>
              {options.map((vocationId) => (
                <Option key={vocationId} vocationId={vocationId} />
              ))}
            </ChipRow>
          </Fragment>
        ))}
      </OptionsContainer>
    </Container>
  );
};

const StyledChip = styled(Chip)`
  background-color: ${({ theme }) => theme.palette.dream.grayLight};
  color: black;

  &:hover {
    background-color: #e0e0e0;
  }

  &.selected {
    background-color: ${({ theme }) => theme.palette.primary.main};
    color: white;

    &:hover {
      background-color: ${({ theme }) => theme.palette.primary.dark};
    }
  }
`;

const Header = styled(Typography)`
  font-weight: 700;
  font-size: 1.25rem;
`;

const ChipRow = styled("div")`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const Container = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const OptionsContainer = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-height: 230px;
  overflow-y: auto;
`;

const OptionListItem = styled(ListItem)`
  width: auto;
  padding: 0;
`;

const AutocompleteTextField = styled(TextField)`
  --border-radius: 1rem;
  --border-size: 2px;
  --input-height: 48px;
  width: 100%;
  background: white;
  border-radius: var(--border-radius);

  & .MuiInput-root {
    height: var(--input-height);
    padding-left: 1rem;
    padding-right: 0 !important;
    border: ${({ theme }) => `var(--border-size) solid ${theme.palette.dream.grayMedium}`};
    border-radius: var(--border-radius);
  }

  & .MuiButton-root {
    border-radius: 0 var(--border-radius) var(--border-radius) 0 !important;
    margin: calc((var(--border-size) * -1));
    margin-right: calc((var(--border-size) * -1) - 1px);
    height: var(--input-height);
    box-shadow: none;
  }
`;

const EndAdornmentContainer = styled("div")`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-right: 1rem;
`;
